import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Compass } from 'lucide-react';
import ActivityListCard from '../components/ActivityListCard';
import Pagination from '../components/Pagination';
import { useActivities } from '../hooks/use-activities';
import { usePaged } from '../hooks/use-paged';
import { useWishlist } from '../context/WishlistContext';
import { useT } from '../i18n/LanguageContext';
import { useLocalizedActivities } from '../i18n/contentLocale';

export default function Favorites() {
  const t = useT();
  const { ids, count, clear } = useWishlist();
  const { data, isLoading } = useActivities({ limit: 200 });
  const rawActivities = useMemo(() => data?.items || [], [data]);
  const activities = useLocalizedActivities(rawActivities);

  // Keep the visitor's own ordering (most recently saved first).
  const saved = useMemo(
    () => ids.map((id) => activities.find((a) => a.id === id)).filter(Boolean),
    [ids, activities]
  );
  const { pageItems, page, setPage, pageCount, pageSize, setPageSize } = usePaged(saved, 9);
  const changePage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="pt-28 pb-20 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-[#c8a25a] text-sm font-semibold tracking-widest uppercase mb-2">{t('wishlist.eyebrow')}</p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#14213d] flex items-center gap-3">
              <Heart className="w-7 h-7 fill-[#c8a25a] text-[#c8a25a]" />
              {t('wishlist.title')}
            </h1>
            <p className="mt-2 text-gray-600">
              {count > 0 ? `${count} ${t('wishlist.countSuffix')}` : t('wishlist.subtitle')}
            </p>
          </div>
          {count > 0 ? (
            <button
              type="button"
              onClick={clear}
              className="self-start rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-red-300 hover:text-red-500"
            >
              {t('wishlist.clearAll')}
            </button>
          ) : null}
        </div>

        {isLoading && count > 0 ? (
          <div className="space-y-4">
            {Array.from({ length: Math.min(count, 4) }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 bg-gray-100 h-40 animate-pulse" />
            ))}
          </div>
        ) : saved.length ? (
          <>
            <div className="space-y-4">
              {pageItems.map((a) => <ActivityListCard key={a.id} activity={a} />)}
            </div>
            <Pagination page={page} pageCount={pageCount} onPageChange={changePage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          </>
        ) : (
          <div className="text-center py-20">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#f7f9fb]">
              <Heart className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-[#14213d]">{t('wishlist.emptyTitle')}</p>
            <p className="mt-2 text-gray-500">{t('wishlist.emptyBody')}</p>
            <Link
              to="/activities"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1fa5a3] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#178a88]"
            >
              <Compass className="h-4 w-4" />
              {t('wishlist.exploreCta')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
